import { type ReactNode, useEffect } from 'react'
import { createPortal } from 'react-dom'

interface ActionModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm?: () => void
  title: string
  description: ReactNode
  confirmText?: string
  cancelText?: string
  testId?: string
  confirmTestId?: string
  cancelTestId?: string
}

/**
 * 通用动作弹窗组件
 * 支持标题、描述、主按钮（确认）和次按钮（取消/关闭）的配置
 */
export default function ActionModal(props: ActionModalProps) {
  // 处理背景锁定
  useEffect(() => {
    if (props.isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [props.isOpen])

  if (!props.isOpen) return null

  return createPortal(
    <>
      <div
        className='fixed inset-0 z-60 bg-black/40 backdrop-blur-sm transition-all duration-300 starting:opacity-0'
        onClick={props.onClose}
        data-testid={`${props.testId}-overlay`}
      />
      <div
        className='fixed bottom-0 left-1/2 z-70 w-full max-w-(--max-width) -translate-x-1/2 rounded-t-[32px] bg-white p-6 pb-[calc(16px+env(safe-area-inset-bottom))] transition-all duration-300 ease-out translate-y-0 starting:translate-y-full'
        style={{
          boxShadow: '0px -8px 24px rgba(0, 0, 0, 0.08)',
        }}
        data-testid={props.testId}
      >
        <div className='flex flex-col items-center gap-[12px]'>
          <div className='mb-2 size-[12px] rounded-full bg-[#6e59cd]/20' />

          <h3
            className='text-center text-[20px] font-black leading-tight text-[#1a1a1a]'
            data-testid={`${props.testId}-title`}
          >
            {props.title}
          </h3>

          <div className='mb-4 text-center text-[14px] leading-relaxed text-[#666]'>
            {props.description}
          </div>

          <div className='flex w-full flex-col gap-[12px]'>
            {props.confirmText && (
              <button
                type='button'
                onClick={() => {
                  props.onConfirm?.()
                  if (!props.cancelText) {
                    props.onClose()
                  }
                }}
                className='flex h-[54px] w-full items-center justify-center rounded-[18px] bg-linear-to-r from-[#a66fff] to-[#5c17f2] text-[16px] font-bold text-white shadow-[0px_8px_16px_rgba(92,23,242,0.25)] active:scale-[0.98] transition-all'
                data-testid={props.confirmTestId || `${props.testId}-confirm`}
              >
                {props.confirmText}
              </button>
            )}

            {props.cancelText && (
              <button
                type='button'
                onClick={props.onClose}
                className='flex h-[54px] w-full items-center justify-center rounded-[18px] bg-[#f5f5f7] text-[16px] font-bold text-[#666] active:scale-[0.98] transition-all'
                data-testid={props.cancelTestId || `${props.testId}-cancel-btn`}
              >
                {props.cancelText}
              </button>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  )
}
